import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Typography,
  Table,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Button,
  Tag,
  Space,
  Tooltip,
  Result,
  message,
} from "antd";
import { SearchOutlined, FileExcelOutlined, EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import {
  listarNovedades,
} from "../../services/novedad_participante/novedadParticipanteService";
import cargarDeptosMunicipios from "../../services/departMunic/deptosMunicipiosServices";
import { selectAppPermissions } from "../../store/auth/selectors";
import NovedadDetalleModal from "./NovedadDetalleModal";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ESTADO_COLOR = { ACTIVA: "success", RESUELTA: "blue", ANULADA: "default" };

const NovedadesListado = () => {
  const permisos = useSelector((state) => selectAppPermissions(state, "novedad_participante"));
  const puedeVer      = permisos.priv_access === "Y";
  const puedeExportar = permisos.priv_export === "Y";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [municipios, setMunicipios] = useState([]);
  const [filtroMunicipio, setFiltroMunicipio] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [filtroDocumento, setFiltroDocumento] = useState("");
  const [rangoFechas, setRangoFechas] = useState(null);

  const [novedadSeleccionada, setNovedadSeleccionada] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    cargarDeptosMunicipios()
      .then((res) => {
        const opciones = (res?.data || []).flatMap((depto) =>
          (depto.municipios || []).map((m) => ({
            value: m.oid_municipio,
            label: `${m.nombre_municipio} (${depto.nombre_departamento})`,
          }))
        );
        setMunicipios(opciones);
      })
      .catch(() => {});
  }, []);

  const construirFiltros = useCallback(
    (paginaActual = page, tamanoActual = pageSize) => ({
      municipio_id: filtroMunicipio || undefined,
      tipo_novedad: filtroTipo || undefined,
      estado: filtroEstado || undefined,
      documento: filtroDocumento || undefined,
      fecha_desde: rangoFechas?.[0] ? rangoFechas[0].format("YYYY-MM-DD") : undefined,
      fecha_hasta: rangoFechas?.[1] ? rangoFechas[1].format("YYYY-MM-DD") : undefined,
      page: paginaActual,
      pageSize: tamanoActual,
    }),
    [filtroMunicipio, filtroTipo, filtroEstado, filtroDocumento, rangoFechas, page, pageSize]
  );

  const consultar = useCallback(
    async (paginaActual = 1, tamanoActual = pageSize) => {
      setLoading(true);
      try {
        const resultado = await listarNovedades(construirFiltros(paginaActual, tamanoActual));
        setRows(resultado.rows || []);
        setTotal(resultado.total || 0);
        setPage(paginaActual);
      } catch (error) {
        console.error(error);
        message.error("Error al cargar las novedades");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [construirFiltros, pageSize]
  );

  useEffect(() => {
    if (puedeVer) consultar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVer]);

  const limpiarFiltros = () => {
    setFiltroMunicipio(null);
    setFiltroTipo("");
    setFiltroEstado(null);
    setFiltroDocumento("");
    setRangoFechas(null);
  };

  const verNovedad = (id) => {
    setNovedadSeleccionada(id);
    setModalVisible(true);
  };

  const exportarExcel = async () => {
    if (!rows.length) {
      message.warning("No hay datos para exportar");
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Novedades");

    const headers = ["Municipio", "Documento", "Nombre", "Tipo de novedad", "Descripción", "Fecha", "Estado"];
    ws.addRow(headers).font = { bold: true };

    rows.forEach((r) => {
      ws.addRow([
        r.municipio_nombre || "",
        r.documento_identificacion,
        r.nombre_completo || "",
        r.tipo_novedad,
        r.descripcion,
        r.fecha_novedad?.slice(0, 10) || "",
        r.estado,
      ]);
    });

    ws.columns.forEach((col) => {
      col.width = Math.max(
        15,
        ...ws.getColumn(col.number).values.map((v) => String(v ?? "").length)
      );
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Novedades_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const columns = useMemo(
    () => [
      { title: "Municipio", dataIndex: "municipio_nombre", key: "municipio_nombre", width: 160 },
      { title: "Documento", dataIndex: "documento_identificacion", key: "documento_identificacion", width: 130 },
      { title: "Nombre", dataIndex: "nombre_completo", key: "nombre_completo", ellipsis: true },
      { title: "Tipo de novedad", dataIndex: "tipo_novedad", key: "tipo_novedad", width: 180 },
      {
        title: "Fecha",
        dataIndex: "fecha_novedad",
        key: "fecha_novedad",
        width: 110,
        render: (v) => v?.slice(0, 10),
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        width: 110,
        align: "center",
        render: (estado) => <Tag color={ESTADO_COLOR[estado]}>{estado}</Tag>,
      },
      {
        title: "Acciones",
        key: "acciones",
        width: 90,
        align: "center",
        render: (_, row) => (
          <Tooltip title="Ver">
            <Button size="small" icon={<EyeOutlined />} onClick={() => verNovedad(row.id)} />
          </Tooltip>
        ),
      },
    ],
    []
  );

  if (!puedeVer) {
    return (
      <Card>
        <Result status="403" title="Acceso no autorizado" subTitle="No tienes permiso para ver este módulo." />
      </Card>
    );
  }

  return (
    <Card>
      <Title level={4}>Novedades</Title>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Select
            placeholder="Municipio"
            style={{ width: "100%" }}
            allowClear
            showSearch
            optionFilterProp="label"
            options={municipios}
            value={filtroMunicipio}
            onChange={setFiltroMunicipio}
          />
        </Col>
        <Col xs={24} sm={12} md={5} lg={4}>
          <Input
            placeholder="Tipo de novedad"
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={5} lg={4}>
          <Select
            placeholder="Estado"
            style={{ width: "100%" }}
            allowClear
            value={filtroEstado}
            onChange={setFiltroEstado}
            options={[
              { value: "ACTIVA", label: "Activa" },
              { value: "RESUELTA", label: "Resuelta" },
              { value: "ANULADA", label: "Anulada" },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={5} lg={4}>
          <Input
            placeholder="N° documento"
            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
            value={filtroDocumento}
            onChange={(e) => setFiltroDocumento(e.target.value.replace(/\D/g, ""))}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <RangePicker style={{ width: "100%" }} value={rangoFechas} onChange={setRangoFechas} />
        </Col>
        <Col xs={24} lg={2}>
          <Space>
            <Tooltip title="Buscar">
              <Button type="primary" icon={<SearchOutlined />} onClick={() => consultar(1)} />
            </Tooltip>
            <Tooltip title="Limpiar filtros">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  limpiarFiltros();
                  consultar(1);
                }}
              />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {puedeExportar && (
        <Space style={{ marginBottom: 12 }}>
          <Button icon={<FileExcelOutlined />} onClick={exportarExcel}>
            Exportar a Excel
          </Button>
        </Space>
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="small"
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nuevaPagina, nuevoTamano) => {
            setPageSize(nuevoTamano);
            consultar(nuevaPagina, nuevoTamano);
          },
        }}
      />

      <NovedadDetalleModal
        open={modalVisible}
        novedadId={novedadSeleccionada}
        onClose={() => setModalVisible(false)}
        onCambio={() => consultar(page)}
      />
    </Card>
  );
};

export default NovedadesListado;
